<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  response: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainResponses/getUIFlags'),
};

const imageUploadRef = ref(null);
const videoUploadRef = ref(null);
const isUploadingImage = ref(false);
const isUploadingVideo = ref(false);
const uploadError = ref('');

const initialState = {
  question: '',
  answer: '',
  media_attachments: [],
};

const state = reactive({ ...initialState });

const validationRules = {
  question: { required, minLength: minLength(1) },
  answer: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.RESPONSES.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  question: getErrorMessage('question', 'QUESTION'),
  answer: getErrorMessage('answer', 'ANSWER'),
}));

const handleCancel = () => emit('cancel');

const prepareDocumentDetails = () => ({
  question: state.question,
  answer: state.answer,
  media_attachments: state.media_attachments,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};

const updateStateFromResponse = response => {
  if (!response) return;

  const { question, answer, media_attachments } = response;

  Object.assign(state, {
    question,
    answer,
    media_attachments: Array.isArray(media_attachments) ? media_attachments : [],
  });
};

watch(
  () => props.response,
  newResponse => {
    if (props.mode === 'edit' && newResponse) {
      updateStateFromResponse(newResponse);
    }
  },
  { immediate: true }
);

const triggerImageUpload = () => {
  uploadError.value = '';
  imageUploadRef.value?.click();
};

const triggerVideoUpload = () => {
  uploadError.value = '';
  videoUploadRef.value?.click();
};

const handleImageFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  isUploadingImage.value = true;
  uploadError.value = '';
  try {
    const { fileUrl } = await uploadFile(file);
    state.media_attachments.push({ url: fileUrl, type: 'image', name: file.name });
  } catch {
    uploadError.value = t('CAPTAIN.RESPONSES.FORM.MEDIA.UPLOAD_ERROR');
  } finally {
    isUploadingImage.value = false;
    event.target.value = '';
  }
};

const handleVideoFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  isUploadingVideo.value = true;
  uploadError.value = '';
  try {
    const { fileUrl } = await uploadFile(file);
    state.media_attachments.push({ url: fileUrl, type: 'video', name: file.name });
  } catch {
    uploadError.value = t('CAPTAIN.RESPONSES.FORM.MEDIA.UPLOAD_ERROR');
  } finally {
    isUploadingVideo.value = false;
    event.target.value = '';
  }
};

const removeMediaItem = index => {
  state.media_attachments.splice(index, 1);
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.question"
      :label="t('CAPTAIN.RESPONSES.FORM.QUESTION.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.QUESTION.PLACEHOLDER')"
      :message="formErrors.question"
      :message-type="formErrors.question ? 'error' : 'info'"
    />
    <Editor
      v-model="state.answer"
      :label="t('CAPTAIN.RESPONSES.FORM.ANSWER.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.ANSWER.PLACEHOLDER')"
      :message="formErrors.answer"
      :max-length="10000"
      :message-type="formErrors.answer ? 'error' : 'info'"
    />

    <!-- Media attachments section -->
    <div class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.RESPONSES.FORM.MEDIA.LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.RESPONSES.FORM.MEDIA.DESCRIPTION') }}
      </p>

      <!-- Uploaded items preview -->
      <div v-if="state.media_attachments.length > 0" class="flex flex-wrap gap-2">
        <div
          v-for="(item, index) in state.media_attachments"
          :key="index"
          class="relative group rounded-lg overflow-hidden border border-n-weak bg-n-alpha-2"
          style="width: 80px; height: 80px;"
        >
          <img
            v-if="item.type === 'image'"
            :src="item.url"
            :alt="item.name"
            class="w-full h-full object-cover"
          />
          <div
            v-else
            class="w-full h-full flex flex-col items-center justify-center gap-1 text-n-slate-11"
          >
            <span class="i-lucide-video w-6 h-6" />
            <span class="text-xs truncate px-1 w-full text-center">{{ item.name }}</span>
          </div>
          <button
            type="button"
            class="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-n-ruby-9 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            @click="removeMediaItem(index)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </div>
      </div>

      <!-- Upload buttons -->
      <div class="flex gap-2">
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 transition-colors disabled:opacity-50"
          :disabled="isUploadingImage"
          @click="triggerImageUpload"
        >
          <span class="i-lucide-image w-4 h-4" />
          {{ isUploadingImage ? '...' : t('CAPTAIN.RESPONSES.FORM.MEDIA.ADD_IMAGE') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 transition-colors disabled:opacity-50"
          :disabled="isUploadingVideo"
          @click="triggerVideoUpload"
        >
          <span class="i-lucide-video w-4 h-4" />
          {{ isUploadingVideo ? '...' : t('CAPTAIN.RESPONSES.FORM.MEDIA.ADD_VIDEO') }}
        </button>
      </div>

      <p v-if="uploadError" class="text-xs text-n-ruby-9">{{ uploadError }}</p>
    </div>

    <!-- Hidden file inputs -->
    <input
      ref="imageUploadRef"
      type="file"
      accept="image/*"
      class="hidden"
      @change="handleImageFileChange"
    />
    <input
      ref="videoUploadRef"
      type="file"
      accept="video/*"
      class="hidden"
      @change="handleVideoFileChange"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
